import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getApiBase } from './config/apiBase';
import { authService } from './services/authService';
import LyricConfirm from './components/LyricConfirm';
import JiraTicketLink from './components/JiraTicketLink';

const apiUrl = getApiBase();

// Shared with the Deploy "Build from scratch" flow so a user's PAT is entered once.
const GITHUB_TOKEN_STORAGE_KEY = 'lyric-build-github-token';

// Platform + framework options for "Build this app". Web frameworks map to the
// deployable languages the scaffolder supports; mobile targets are code scaffolds.
const BUILD_FRAMEWORKS = {
  web: [
    { id: 'node', label: 'Node.js + Express' },
    { id: 'python', label: 'Python (Flask)' },
    { id: 'go', label: 'Go' },
  ],
  ios: [{ id: 'swiftui', label: 'SwiftUI' }],
  android: [{ id: 'compose', label: 'Jetpack Compose (Kotlin)' }],
};
const BUILD_PLATFORMS = [
  { id: 'web', label: '🌐 Web app' },
  { id: 'ios', label: '🍎 iOS (Swift)' },
  { id: 'android', label: '🤖 Android (Kotlin)' },
];

const toRepoSlug = (name) => String(name || '').trim().toLowerCase()
  .replace(/[^a-z0-9._-]+/g, '-').replace(/^[-._]+|[-._]+$/g, '').slice(0, 100);

const authenticatedFetch = async (url, options = {}) => {
  const token = await authService.getAccessToken();
  return fetch(url, {
    ...options,
    headers: {
      Authorization: 'Bearer ' + token,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
};

// Convert the HTML description (already converted from Jira ADF by the server)
// into readable plain text for the AI "Describe it" prompt — block tags become
// line breaks and list items get bullets, then all remaining tags are dropped.
const htmlToText = (html) => {
  if (!html) return '';
  const withBreaks = String(html)
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\s*li[^>]*>/gi, '• ')
    .replace(/<\/\s*(p|div|li|h[1-6]|tr)\s*>/gi, '\n');
  const el = document.createElement('div');
  el.innerHTML = withBreaks;
  return (el.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
};

/*
 * The editor style + script injected into the live preview iframe. They are set
 * into the iframe's srcDoc ONCE per document load; after that, all instant edits
 * happen inside the iframe DOM (no reload, no server round-trip) and the iframe
 * posts the serialized clean HTML back to the parent. On serialize the injected
 * bits and editor-only attributes are stripped so the saved HTML stays clean.
 */
const EDITOR_STYLE = `<style id="__mock_editor_style">
  .__mock_hover { outline: 2px dashed #93c5fd !important; outline-offset: 1px; cursor: grab !important; }
  .__mock_selected { outline: 2px solid #1a56db !important; outline-offset: 1px; cursor: grab !important; }
  .__mock_editing { outline: 2px solid #047857 !important; outline-offset: 1px; cursor: text !important; }
  .__mock_dragging { opacity: .9 !important; }
  .__mock_drag_active, .__mock_drag_active * { cursor: grabbing !important; user-select: none !important; }
</style>`;

const EDITOR_SCRIPT = `<script id="__mock_editor_script">
(function(){
  var selectedUid = null;
  var insertSeq = 0;
  var suppressClick = false;
  var drag = null; // active drag: { el, startX, startY, baseTx, baseTy, moved }
  function num(v){ var n = parseFloat(v); return isNaN(n) ? 0 : n; }
  function post(msg){ msg.__mock = true; parent.postMessage(msg, '*'); }
  function uidAll(){
    var i = 0;
    var all = document.body ? document.body.querySelectorAll('*') : [];
    for (var k = 0; k < all.length; k++) {
      var el = all[k];
      if (el.id === '__mock_editor_script' || el.id === '__mock_editor_style') continue;
      if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE') continue;
      if (!el.getAttribute('data-mock-uid')) {
        el.setAttribute('data-mock-uid', 'm' + (i++) + '_' + Date.now().toString(36) + '_' + Math.floor(Math.random()*1e4));
      }
    }
  }
  function elByUid(uid){ return uid ? document.querySelector('[data-mock-uid="' + uid + '"]') : null; }
  function clearSel(){
    var prev = document.querySelectorAll('.__mock_selected');
    for (var i = 0; i < prev.length; i++) prev[i].classList.remove('__mock_selected');
  }
  function selectEl(el){
    clearSel();
    if (!el || el === document.body) { selectedUid = null; post({ type: 'select', uid: null }); return; }
    el.classList.add('__mock_selected');
    selectedUid = el.getAttribute('data-mock-uid');
    var cs = getComputedStyle(el);
    post({
      type: 'select',
      uid: selectedUid,
      tag: el.tagName.toLowerCase(),
      text: (el.textContent || '').trim().slice(0, 300),
      childCount: el.children ? el.children.length : 0,
      outer: el.outerHTML.slice(0, 5000),
      styles: {
        color: cs.color,
        background: cs.backgroundColor,
        fontSize: parseInt(cs.fontSize, 10) || 16,
        fontWeight: cs.fontWeight,
        textAlign: cs.textAlign
      }
    });
  }
  function finishEditing(el){
    if (!el || !el.getAttribute || el.getAttribute('contenteditable') !== 'true') return;
    el.removeAttribute('contenteditable');
    el.classList.remove('__mock_editing');
    uidAll();
    if (el.getAttribute('data-mock-uid') === selectedUid) selectEl(el);
    serialize();
  }
  document.addEventListener('mouseover', function(e){ if (drag && drag.moved) return; if (e.target !== document.body) e.target.classList.add('__mock_hover'); }, true);
  document.addEventListener('mouseout', function(e){ if (drag && drag.moved) return; e.target.classList.remove('__mock_hover'); }, true);
  document.addEventListener('click', function(e){
    var el = e.target;
    // While an element is being edited inline, let clicks place the caret.
    if (el && el.getAttribute && el.getAttribute('contenteditable') === 'true') { e.stopPropagation(); return; }
    e.preventDefault(); e.stopPropagation();
    // Swallow the click that ends a drag so it doesn't re-trigger selection logic.
    if (suppressClick) { suppressClick = false; return; }
    selectEl(el);
  }, true);
  // Double-click any element to edit its text live, right in the canvas.
  document.addEventListener('dblclick', function(e){
    e.preventDefault(); e.stopPropagation();
    var el = e.target;
    if (!el || el === document.body) return;
    selectEl(el);
    el.setAttribute('contenteditable', 'true');
    el.classList.add('__mock_editing');
    el.focus();
  }, true);
  // Finish inline editing on blur, and support Enter / Escape to commit.
  document.addEventListener('focusout', function(e){ finishEditing(e.target); }, true);
  document.addEventListener('keydown', function(e){
    var el = e.target;
    if (el && el.getAttribute && el.getAttribute('contenteditable') === 'true') {
      if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Escape') { e.preventDefault(); el.blur(); }
    }
  }, true);
  // --- Drag to move -------------------------------------------------------
  // Click-and-drag any element to reposition it. Uses the same transform-
  // translate model (data-mock-tx / data-mock-ty) as the arrow buttons, so the
  // two stay perfectly in sync. A 3px threshold preserves plain clicks (select)
  // and double-clicks (edit).
  document.addEventListener('mousedown', function(e){
    if (e.button !== 0) return;
    var el = e.target;
    if (!el || el === document.body || !el.getAttribute) return;
    if (el.getAttribute('contenteditable') === 'true') return; // don't hijack text editing
    drag = {
      el: el,
      startX: e.clientX,
      startY: e.clientY,
      baseTx: num(el.getAttribute('data-mock-tx')),
      baseTy: num(el.getAttribute('data-mock-ty')),
      moved: false
    };
  }, true);
  document.addEventListener('mousemove', function(e){
    if (!drag) return;
    var dx = e.clientX - drag.startX;
    var dy = e.clientY - drag.startY;
    if (!drag.moved) {
      if (Math.abs(dx) < 3 && Math.abs(dy) < 3) return; // below threshold — not a drag yet
      drag.moved = true;
      selectEl(drag.el);
      drag.el.classList.add('__mock_dragging');
      if (document.body) document.body.classList.add('__mock_drag_active');
    }
    e.preventDefault();
    var tx = drag.baseTx + dx;
    var ty = drag.baseTy + dy;
    drag.el.setAttribute('data-mock-tx', tx);
    drag.el.setAttribute('data-mock-ty', ty);
    drag.el.style.transform = 'translate(' + tx + 'px, ' + ty + 'px)';
  }, true);
  document.addEventListener('mouseup', function(){
    if (!drag) return;
    if (drag.moved) {
      drag.el.classList.remove('__mock_dragging');
      if (document.body) document.body.classList.remove('__mock_drag_active');
      suppressClick = true; // swallow the trailing click
      uidAll();
      selectEl(drag.el);
      serialize();
    }
    drag = null;
  }, true);

  function applyOp(op){
    if (op.kind === 'insert') { insertSnippet(op); return; }
    var el = op.uid ? elByUid(op.uid) : elByUid(selectedUid);
    if (!el) return;
    switch (op.kind) {
      case 'move': {
        var tx = num(el.getAttribute('data-mock-tx')) + (op.dx || 0);
        var ty = num(el.getAttribute('data-mock-ty')) + (op.dy || 0);
        el.setAttribute('data-mock-tx', tx);
        el.setAttribute('data-mock-ty', ty);
        el.style.transform = 'translate(' + tx + 'px, ' + ty + 'px)';
        break;
      }
      case 'color': el.style.color = op.value; break;
      case 'background': el.style.backgroundColor = op.value; break;
      case 'fontSize': {
        var cur = num(getComputedStyle(el).fontSize) || 16;
        el.style.fontSize = Math.max(6, cur + (op.delta || 0)) + 'px';
        break;
      }
      case 'fontSizeAbs': el.style.fontSize = Math.max(6, num(op.value) || 16) + 'px'; break;
      case 'fontWeight': el.style.fontWeight = op.value; break;
      case 'width': {
        var cw = el.getBoundingClientRect().width;
        el.style.width = Math.max(8, cw + (op.delta || 0)) + 'px';
        break;
      }
      case 'padding': {
        var cp = num(getComputedStyle(el).paddingTop) || 0;
        el.style.padding = Math.max(0, cp + (op.delta || 0)) + 'px';
        break;
      }
      case 'align': el.style.textAlign = op.value; break;
      case 'setText': el.textContent = op.value; break;
      case 'delete': if (el.parentNode) { el.parentNode.removeChild(el); selectEl(null); } break;
      case 'duplicate': {
        var c = el.cloneNode(true);
        c.classList.remove('__mock_selected');
        c.removeAttribute('data-mock-uid');
        if (el.parentNode) el.parentNode.insertBefore(c, el.nextSibling);
        break;
      }
      default: break;
    }
    uidAll();
    serialize();
  }
  // Insert a new element snippet into the UPPER-RIGHT corner of the canvas.
  // Each element is absolutely positioned there (with a small cascade so
  // multiple inserts don't perfectly overlap), then selected so the user can
  // immediately drag/style it. The body is made a positioning context first.
  function insertSnippet(op){
    var container = document.createElement('div');
    container.innerHTML = op.html || '';
    var node = container.firstElementChild;
    if (!node) return;
    if (getComputedStyle(document.body).position === 'static') {
      document.body.style.position = 'relative';
    }
    var off = (insertSeq % 8) * 18;
    insertSeq++;
    node.style.position = 'absolute';
    node.style.top = (16 + off) + 'px';
    node.style.right = (16 + off) + 'px';
    node.style.zIndex = String(50 + insertSeq);
    document.body.appendChild(node);
    uidAll();
    selectEl(node);
    serialize();
  }
  function serialize(){
    var clone = document.documentElement.cloneNode(true);
    var rm = clone.querySelectorAll('#__mock_editor_script, #__mock_editor_style');
    for (var i = 0; i < rm.length; i++) rm[i].remove();
    var tagged = clone.querySelectorAll('[data-mock-uid]');
    for (var j = 0; j < tagged.length; j++) {
      tagged[j].removeAttribute('data-mock-uid');
      tagged[j].removeAttribute('data-mock-tx');
      tagged[j].removeAttribute('data-mock-ty');
    }
    var sel = clone.querySelectorAll('.__mock_selected, .__mock_hover, .__mock_editing');
    for (var m = 0; m < sel.length; m++) {
      sel[m].classList.remove('__mock_selected');
      sel[m].classList.remove('__mock_hover');
      sel[m].classList.remove('__mock_editing');
      sel[m].removeAttribute('contenteditable');
      if (sel[m].getAttribute('class') === '') sel[m].removeAttribute('class');
    }
    var edit = clone.querySelectorAll('[contenteditable]');
    for (var q = 0; q < edit.length; q++) edit[q].removeAttribute('contenteditable');
    post({ type: 'serialized', html: '<!DOCTYPE html>\\n' + clone.outerHTML });
  }
  window.addEventListener('message', function(e){
    var d = e.data || {};
    if (!d.__mockcmd) return;
    if (d.type === 'apply') applyOp(d.op);
    else if (d.type === 'serialize') serialize();
    else if (d.type === 'selectUid') { var el = elByUid(d.uid); if (el) selectEl(el); }
    else if (d.type === 'deselect') selectEl(null);
  });
  uidAll();
  post({ type: 'ready' });
})();
</script>`;

// Inject the editor style (into <head>) and script (before </body>) into a
// mockup document so the live preview becomes interactively editable.
function wrapWithEditor(html) {
  let doc = String(html || '');
  if (doc.includes('</head>')) doc = doc.replace('</head>', `${EDITOR_STYLE}</head>`);
  else doc = EDITOR_STYLE + doc;
  if (doc.includes('</body>')) doc = doc.replace('</body>', `${EDITOR_SCRIPT}</body>`);
  else doc += EDITOR_SCRIPT;
  return doc;
}

const BLANK_HTML = `<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>Mockup</title>
<style>
  body { margin:0; font-family:-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif; background:#f7f8fa; color:#1f2937; }
  .canvas { min-height:100vh; padding:40px; }
  .hint { color:#9ca3af; text-align:center; margin-top:120px; font-size:15px; }
</style></head>
<body><div class="canvas"><p class="hint">Blank canvas — click an element to select it, then use the command bar or AI to edit. Use “Add with AI” to build something new.</p></div></body></html>`;

const TEMPLATES = {
  landing: {
    label: 'Landing page',
    html: `<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>Landing</title>
<style>
  body{margin:0;font-family:-apple-system,"Segoe UI",Roboto,sans-serif;color:#1f2937;}
  header{display:flex;justify-content:space-between;align-items:center;padding:18px 40px;background:#fff;box-shadow:0 1px 3px rgba(0,0,0,.08);}
  .logo{font-weight:800;font-size:20px;color:#1a56db;}
  nav a{margin-left:22px;color:#374151;text-decoration:none;font-size:14px;}
  .hero{text-align:center;padding:90px 24px;background:linear-gradient(135deg,#1a56db,#3b82f6);color:#fff;}
  .hero h1{font-size:40px;margin:0 0 14px;}
  .hero p{font-size:18px;opacity:.92;max-width:560px;margin:0 auto 28px;}
  .btn{display:inline-block;background:#fff;color:#1a56db;padding:13px 28px;border-radius:8px;font-weight:700;text-decoration:none;}
  .features{display:flex;gap:24px;justify-content:center;flex-wrap:wrap;padding:60px 24px;background:#f7f8fa;}
  .card{background:#fff;border-radius:12px;padding:26px;width:260px;box-shadow:0 2px 10px rgba(0,0,0,.06);}
  .card h3{margin:0 0 8px;font-size:18px;}
  .card p{margin:0;color:#6b7280;font-size:14px;}
</style></head>
<body>
  <header><div class="logo">Acme</div><nav><a href="#">Product</a><a href="#">Pricing</a><a href="#">Docs</a><a href="#">Sign in</a></nav></header>
  <section class="hero"><h1>Build faster with Acme</h1><p>The all-in-one platform to design, ship and scale your product.</p><a class="btn" href="#">Get started free</a></section>
  <section class="features">
    <div class="card"><h3>Fast</h3><p>Blazing performance out of the box with zero config.</p></div>
    <div class="card"><h3>Secure</h3><p>Enterprise-grade security and compliance built in.</p></div>
    <div class="card"><h3>Simple</h3><p>An intuitive interface your whole team will love.</p></div>
  </section>
</body></html>`,
  },
  dashboard: {
    label: 'Dashboard',
    html: `<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>Dashboard</title>
<style>
  body{margin:0;font-family:-apple-system,"Segoe UI",Roboto,sans-serif;color:#1f2937;background:#f3f4f6;}
  .layout{display:flex;min-height:100vh;}
  .side{width:220px;background:#111827;color:#e5e7eb;padding:22px 16px;}
  .side .brand{font-weight:800;font-size:18px;margin-bottom:26px;color:#fff;}
  .side a{display:block;color:#9ca3af;text-decoration:none;padding:10px 12px;border-radius:8px;font-size:14px;margin-bottom:4px;}
  .side a.active{background:#1f2937;color:#fff;}
  .main{flex:1;padding:26px 32px;}
  .topbar{display:flex;justify-content:space-between;align-items:center;margin-bottom:24px;}
  .topbar h1{margin:0;font-size:22px;}
  .stats{display:flex;gap:18px;flex-wrap:wrap;}
  .stat{background:#fff;border-radius:12px;padding:20px 22px;flex:1 1 160px;box-shadow:0 1px 4px rgba(0,0,0,.06);}
  .stat .k{color:#6b7280;font-size:13px;}
  .stat .v{font-size:28px;font-weight:800;margin-top:6px;}
  .panel{background:#fff;border-radius:12px;padding:22px;margin-top:22px;box-shadow:0 1px 4px rgba(0,0,0,.06);}
</style></head>
<body>
  <div class="layout">
    <aside class="side"><div class="brand">Acme</div><a class="active" href="#">Overview</a><a href="#">Analytics</a><a href="#">Customers</a><a href="#">Settings</a></aside>
    <main class="main">
      <div class="topbar"><h1>Overview</h1><div>Jane Doe ▾</div></div>
      <div class="stats">
        <div class="stat"><div class="k">Revenue</div><div class="v">$48.2k</div></div>
        <div class="stat"><div class="k">Active users</div><div class="v">1,284</div></div>
        <div class="stat"><div class="k">Conversion</div><div class="v">3.6%</div></div>
      </div>
      <div class="panel"><h3 style="margin-top:0">Recent activity</h3><p style="color:#6b7280">A chart or table would go here.</p></div>
    </main>
  </div>
</body></html>`,
  },
  form: {
    label: 'Form / settings',
    html: `<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>Form</title>
<style>
  body{margin:0;font-family:-apple-system,"Segoe UI",Roboto,sans-serif;color:#1f2937;background:#f7f8fa;padding:40px 16px;}
  .card{max-width:520px;margin:0 auto;background:#fff;border-radius:14px;padding:32px;box-shadow:0 2px 12px rgba(0,0,0,.07);}
  h1{margin:0 0 4px;font-size:24px;}
  .sub{color:#6b7280;margin:0 0 24px;font-size:14px;}
  label{display:block;font-size:13px;font-weight:600;margin:16px 0 6px;}
  input,select,textarea{width:100%;box-sizing:border-box;padding:11px 12px;border:1px solid #d1d5db;border-radius:8px;font-size:14px;}
  .row{display:flex;gap:14px;}
  .row>div{flex:1;}
  .btn{margin-top:24px;background:#1a56db;color:#fff;border:none;padding:12px 22px;border-radius:8px;font-weight:700;font-size:15px;cursor:pointer;}
</style></head>
<body>
  <div class="card">
    <h1>Account settings</h1><p class="sub">Update your profile and preferences.</p>
    <div class="row"><div><label>First name</label><input value="Jane"></div><div><label>Last name</label><input value="Doe"></div></div>
    <label>Email</label><input value="jane@acme.com">
    <label>Role</label><select><option>Admin</option><option>Member</option><option>Viewer</option></select>
    <label>Bio</label><textarea rows="3">Product designer at Acme.</textarea>
    <button class="btn">Save changes</button>
  </div>
</body></html>`,
  },
  mobile: {
    label: 'Mobile screen',
    html: `<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>Mobile</title>
<style>
  body{margin:0;font-family:-apple-system,"Segoe UI",Roboto,sans-serif;color:#1f2937;background:#eef1f5;}
  .screen{max-width:390px;margin:0 auto;min-height:100vh;background:#fff;}
  .status{display:flex;justify-content:space-between;padding:10px 18px;font-size:12px;color:#6b7280;}
  .head{padding:14px 18px;font-size:22px;font-weight:800;}
  .search{margin:0 18px 16px;padding:11px 14px;background:#f3f4f6;border-radius:10px;color:#9ca3af;font-size:14px;}
  .list{padding:0 18px;}
  .item{display:flex;align-items:center;gap:12px;padding:14px 0;border-bottom:1px solid #f0f0f0;}
  .avatar{width:42px;height:42px;border-radius:50%;background:#dbeafe;flex-shrink:0;}
  .item .t{font-weight:600;font-size:15px;}
  .item .d{color:#9ca3af;font-size:13px;}
  .tabbar{position:sticky;bottom:0;display:flex;justify-content:space-around;padding:12px 0;border-top:1px solid #eee;background:#fff;color:#6b7280;font-size:12px;}
</style></head>
<body>
  <div class="screen">
    <div class="status"><span>9:41</span><span>100%</span></div>
    <div class="head">Messages</div>
    <div class="search">Search</div>
    <div class="list">
      <div class="item"><div class="avatar"></div><div><div class="t">Alex Kim</div><div class="d">See you at 3pm 👍</div></div></div>
      <div class="item"><div class="avatar"></div><div><div class="t">Design team</div><div class="d">New mockups are ready</div></div></div>
      <div class="item"><div class="avatar"></div><div><div class="t">Sam Lee</div><div class="d">Thanks!</div></div></div>
    </div>
    <div class="tabbar"><span>Chats</span><span>Calls</span><span>Settings</span></div>
  </div>
</body></html>`,
  },
};

// Quick-insert palette: small library of basic building blocks the user can
// drop into the canvas with one click. Each snippet is a single root element so
// the iframe inserter can select it after insertion.
const PALETTE_ELEMENTS = [
  { label: 'Heading', icon: 'H', html: '<h2 style="font-size:22px;font-weight:700;color:#111827;margin:8px 0;">Heading</h2>' },
  { label: 'Text', icon: '¶', html: '<p style="font-size:14px;line-height:1.5;color:#374151;margin:8px 0;">Text paragraph — double-click to edit.</p>' },
  { label: 'Button', icon: '⬛', html: '<button style="padding:10px 16px;background:#1a56db;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;">Button</button>' },
  { label: 'Input', icon: '▭', html: '<input type="text" placeholder="Enter text…" style="padding:9px 11px;border:1px solid #cbd5e1;border-radius:6px;font-size:14px;min-width:200px;" />' },
  { label: 'Link', icon: '🔗', html: '<a href="#" style="color:#1a56db;text-decoration:underline;font-size:14px;">Link text</a>' },
  { label: 'Image', icon: '🖼', html: '<div style="width:220px;height:130px;background:#e5e7eb;border-radius:8px;display:flex;align-items:center;justify-content:center;color:#6b7280;font-size:13px;">🖼 Image</div>' },
  { label: 'Card', icon: '▢', html: '<div style="padding:16px;border:1px solid #e5e7eb;border-radius:10px;background:#fff;box-shadow:0 1px 4px rgba(0,0,0,.06);max-width:320px;"><div style="font-weight:700;color:#111827;margin-bottom:6px;">Card title</div><div style="font-size:14px;color:#4b5563;">Card body content goes here.</div></div>' },
  { label: 'Badge', icon: '◍', html: '<span style="display:inline-block;padding:3px 10px;border-radius:12px;background:#eef2ff;color:#1e40af;font-size:12px;font-weight:600;">Badge</span>' },
  { label: 'Divider', icon: '―', html: '<hr style="border:none;border-top:1px solid #e5e7eb;margin:16px 0;" />' },
  { label: 'Row', icon: '▥', html: '<div style="display:flex;gap:12px;align-items:center;padding:8px 0;"><div style="flex:1;padding:12px;background:#f3f4f6;border-radius:8px;text-align:center;">Column 1</div><div style="flex:1;padding:12px;background:#f3f4f6;border-radius:8px;text-align:center;">Column 2</div></div>' },
];

// Common CSS color names recognized by the instant command parser.
const COLOR_WORDS = ['black', 'white', 'red', 'green', 'blue', 'yellow', 'orange', 'purple', 'pink',
  'gray', 'grey', 'teal', 'cyan', 'magenta', 'brown', 'navy', 'maroon', 'lime', 'olive', 'silver',
  'gold', 'indigo', 'violet', 'crimson', 'coral', 'salmon', 'khaki', 'beige', 'turquoise', 'transparent'];

// Convert a computed CSS color (rgb/rgba) to a #rrggbb hex for <input type="color">.
// Falls back to a sensible default (used for transparent backgrounds etc.).
function rgbToHex(value, fallback = '#ffffff') {
  if (!value) return fallback;
  const v = String(value).trim();
  if (v.startsWith('#')) return v.length === 4
    ? `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`
    : v.slice(0, 7);
  const m = v.match(/rgba?\(([^)]+)\)/);
  if (!m) return fallback;
  const parts = m[1].split(',').map((x) => x.trim());
  const a = parts[3] !== undefined ? parseFloat(parts[3]) : 1;
  if (a === 0) return fallback; // fully transparent
  const hex = parts.slice(0, 3).map((c) => {
    const h = Math.max(0, Math.min(255, parseInt(c, 10) || 0)).toString(16);
    return h.length === 1 ? `0${h}` : h;
  }).join('');
  return `#${hex}`;
}

// Parse a natural-language command into an instant DOM op, or null if it needs
// the (slower) AI edit path. Instant ops are applied inside the iframe with no
// server round-trip, so simple layout/style tweaks feel immediate.
function parseCommand(input) {
  const t = String(input || '').trim().toLowerCase();
  if (!t) return null;
  const numMatch = t.match(/(\d+(?:\.\d+)?)/);
  const n = numMatch ? parseFloat(numMatch[1]) : null;

  if (/\b(delete|remove|hide)\b/.test(t)) return { kind: 'delete' };
  if (/\b(duplicate|copy|clone)\b/.test(t)) return { kind: 'duplicate' };

  if (/\b(move|nudge|shift|up|down|left|right)\b/.test(t)) {
    let dx = 0; let dy = 0; let matched = false;
    const d = n != null ? n : 10;
    if (/\bdown\b/.test(t)) { dy = d; matched = true; }
    if (/\bup\b/.test(t)) { dy = -d; matched = true; }
    if (/\bright\b/.test(t)) { dx = d; matched = true; }
    if (/\bleft\b/.test(t)) { dx = -d; matched = true; }
    if (matched) return { kind: 'move', dx, dy };
  }

  if (/\b(center|centre)\b/.test(t)) return { kind: 'align', value: 'center' };
  if (/\bleft[- ]?align/.test(t)) return { kind: 'align', value: 'left' };
  if (/\bright[- ]?align/.test(t)) return { kind: 'align', value: 'right' };

  if (/\bwider\b/.test(t)) return { kind: 'width', delta: n != null ? n : 20 };
  if (/\bnarrower\b/.test(t)) return { kind: 'width', delta: -(n != null ? n : 20) };

  if (/(more|add)\s+(padding|space|room)/.test(t)) return { kind: 'padding', delta: n != null ? n : 6 };
  if (/(less|reduce|remove)\s+(padding|space|room)/.test(t)) return { kind: 'padding', delta: -(n != null ? n : 6) };

  if (/\b(bigger|larger|grow|increase)\b/.test(t) && !/\bwid/.test(t)) return { kind: 'fontSize', delta: n != null ? n : 2 };
  if (/\b(smaller|shrink|decrease|tinier)\b/.test(t) && !/\bwid/.test(t)) return { kind: 'fontSize', delta: -(n != null ? n : 2) };

  const hex = t.match(/#[0-9a-f]{3,8}/);
  const colorWord = COLOR_WORDS.find((c) => new RegExp(`\\b${c}\\b`).test(t));
  const colorVal = hex ? hex[0] : colorWord;
  if (colorVal) {
    if (/\b(background|bg|fill|behind)\b/.test(t)) return { kind: 'background', value: colorVal };
    return { kind: 'color', value: colorVal };
  }

  return null; // fall back to AI
}

/**
 * The Design Mockup builder (SDLC flow step). Renders a live, click-to-select
 * HTML preview in an iframe. Simple edits ("move it down 5", "make it blue",
 * "duplicate", "bigger") apply instantly inside the iframe with no server call;
 * anything else falls back to a Gemini edit of the whole document. New mockups
 * can be started blank, from a template, or generated from a description.
 */
const MockupBuilder = ({ flow, step, selectedProject, selectedModel, onSave, initialDescribe = '', embedded = false }) => {
  const docType = step?.docType || 'MOCKUP';
  const existing = flow?.deliverables?.[docType] || null;

  const [html, setHtml] = useState(existing?.html || '');
  const [srcDoc, setSrcDoc] = useState(existing?.html ? wrapWithEditor(existing.html) : '');
  // User-adjustable preview height (drag the handle below the canvas to resize
  // vertically). Applies to both desktop and mobile frames.
  const [previewHeight, setPreviewHeight] = useState(560);
  const [started, setStarted] = useState(Boolean(existing?.html));
  const [device, setDevice] = useState('desktop'); // 'desktop' | 'mobile'
  const [selected, setSelected] = useState(null);
  const [iframeReady, setIframeReady] = useState(false);
  const [command, setCommand] = useState('');
  const [describe, setDescribe] = useState(initialDescribe || '');
  const [context, setContext] = useState('');
  // Optional reference screenshot the AI should match. { name, dataUrl } | null.
  const [refImage, setRefImage] = useState(null);
  const [imageError, setImageError] = useState(null);
  const imageInputRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modelUsed, setModelUsed] = useState(existing?.modelUsed || null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Jira Stories linked to this mockup (persisted on the deliverable). A mockup
  // can cover several stories, so this is a set of keys the user picks below.
  const [linkedStories, setLinkedStories] = useState(
    Array.isArray(existing?.linkedStories) ? existing.linkedStories : []
  );
  const [storySearch, setStorySearch] = useState('');
  const [storyResults, setStoryResults] = useState([]);
  const [storySearching, setStorySearching] = useState(false);
  const [storyMeta, setStoryMeta] = useState({}); // key -> summary, for display
  const [storyMsg, setStoryMsg] = useState(null);
  // Collapse the whole Linked Stories panel to give the UI builder more room.
  // Defaults to collapsed when stories are already linked (returning users).
  const [storiesCollapsed, setStoriesCollapsed] = useState(
    () => Array.isArray(existing?.linkedStories) && existing.linkedStories.length > 0
  );

  // "Push to Jira" modal: attach this mockup (image + link) to linked Stories.
  const [pushOpen, setPushOpen] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [pushResults, setPushResults] = useState(null);
  const [pushError, setPushError] = useState(null);
  const [pushSel, setPushSel] = useState([]);
  const [pushImage, setPushImage] = useState(true);
  const [pushLink, setPushLink] = useState(true);

  // "Build this app" modal: turn the Epic + Stories + this mockup into a repo.
  const [buildOpen, setBuildOpen] = useState(false);
  const [building, setBuilding] = useState(false);
  const [buildResult, setBuildResult] = useState(null);
  const [buildError, setBuildError] = useState(null);
  const [buildTokenSetup, setBuildTokenSetup] = useState(null);
  const [buildPlatform, setBuildPlatform] = useState('web');
  const [buildFramework, setBuildFramework] = useState('node');
  const [buildAppName, setBuildAppName] = useState('');
  const [buildRepoName, setBuildRepoName] = useState('');
  const [githubToken, setGithubToken] = useState(() => {
    try { return localStorage.getItem(GITHUB_TOKEN_STORAGE_KEY) || ''; } catch { return ''; }
  });
  const [rememberToken, setRememberToken] = useState(true);

  // Live Inspector drafts (initialised from the selected element's styles so the
  // native controls prefill correctly, and decoupled so dragging a picker/slider
  // doesn't fight the value prop).
  const [textDraft, setTextDraft] = useState('');
  const [colorDraft, setColorDraft] = useState('#000000');
  const [bgDraft, setBgDraft] = useState('#ffffff');
  const [fontDraft, setFontDraft] = useState(16);
  const [boldOn, setBoldOn] = useState(false);
  const [alignVal, setAlignVal] = useState('left');

  // Quick-insert palette: a compact panel of basic elements, docked to the left
  // of the canvas area. It lives in the page flow (scrolls with the whole screen)
  // rather than floating over the viewport.
  const [paletteMin, setPaletteMin] = useState(false);

  const iframeRef = useRef(null);

  // Seed the AI context box from the flow name only (editable). Jira ticket
  // keys are deliberately NOT seeded — they are meaningless to a visual design
  // and the model tends to render them (and status chrome) into the mockup.
  useEffect(() => {
    setContext(flow?.name ? `Feature: ${flow.name}` : '');
  }, [flow?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync Inspector controls to the newly selected element's current styles.
  useEffect(() => {
    if (!selected) return;
    const st = selected.styles || {};
    setTextDraft(selected.text || '');
    setColorDraft(rgbToHex(st.color, '#000000'));
    setBgDraft(rgbToHex(st.background, '#ffffff'));
    setFontDraft(st.fontSize || 16);
    setBoldOn(String(st.fontWeight) === 'bold' || parseInt(st.fontWeight, 10) >= 600);
    setAlignVal(['left', 'center', 'right', 'justify'].includes(st.textAlign) ? st.textAlign : 'left');
  }, [selected?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const postToIframe = useCallback((msg) => {
    const win = iframeRef.current && iframeRef.current.contentWindow;
    if (win) win.postMessage({ ...msg, __mockcmd: true }, '*');
  }, []);

  // Listen for messages coming back from the iframe editor script.
  useEffect(() => {
    const onMessage = (e) => {
      const win = iframeRef.current && iframeRef.current.contentWindow;
      if (!e.data || !e.data.__mock || e.source !== win) return;
      const d = e.data;
      if (d.type === 'ready') setIframeReady(true);
      else if (d.type === 'select') setSelected(d.uid ? { uid: d.uid, tag: d.tag, text: d.text, outer: d.outer, childCount: d.childCount || 0, styles: d.styles || {} } : null);
      else if (d.type === 'serialized') setHtml(d.html);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Debounced, project-scoped Jira Story search for the picker. Only Stories are
  // shown; already-linked ones are filtered out when rendering the results.
  useEffect(() => {
    const q = storySearch.trim();
    if (!selectedProject || q.length < 2) { setStoryResults([]); setStorySearching(false); return undefined; }
    let cancelled = false;
    setStorySearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await authenticatedFetch(
          `${apiUrl}/api/search-issues?projectKey=${encodeURIComponent(selectedProject)}&query=${encodeURIComponent(q)}`
        );
        const data = await res.json();
        if (cancelled) return;
        const stories = (res.ok && data.success ? data.issues || [] : [])
          .filter((i) => (i.fields?.issuetype?.name || '').toLowerCase() === 'story');
        setStoryResults(stories);
        if (stories.length) {
          setStoryMeta((m) => {
            const next = { ...m };
            stories.forEach((i) => { next[i.key] = i.fields?.summary || ''; });
            return next;
          });
        }
      } catch {
        if (!cancelled) setStoryResults([]);
      } finally {
        if (!cancelled) setStorySearching(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [storySearch, selectedProject]);

  // Persist the linked-story set on the deliverable without disturbing the
  // mockup's own HTML/phase (the server keeps those when they're omitted).
  const persistLinkedStories = async (nextKeys) => {
    setLinkedStories(nextKeys);
    if (!onSave) return;
    try {
      await onSave(docType, {
        linkedStories: nextKeys,
        status: existing?.status || (started ? 'in_progress' : 'not_started'),
      });
    } catch (err) {
      setError(err.message || 'Failed to save linked stories');
    }
  };

  const normKey = (k) => String(k || '').trim().toUpperCase();
  const addStory = (key, summary) => {
    const k = normKey(key);
    if (!k || linkedStories.includes(k)) return;
    if (summary !== undefined) setStoryMeta((m) => ({ ...m, [k]: summary }));
    setStorySearch('');
    setStoryResults([]);
    setStoryMsg(`Linked ${k}.`);
    persistLinkedStories([...linkedStories, k]);
    seedDescribeFromStory(k, summary);
  };
  // On linking a Story, pull its summary + description and seed the AI "Describe
  // it" prompt (only before the mockup is started, since that's the only time
  // the box is shown). Multiple stories accumulate, separated by a divider.
  const seedDescribeFromStory = async (key, knownSummary) => {
    try {
      const res = await authenticatedFetch(`${apiUrl}/api/issue/${encodeURIComponent(key)}`);
      const data = await res.json();
      if (!res.ok || !data.success) return;
      const f = data.issue?.fields || {};
      const sum = f.summary || knownSummary || '';
      if (sum) setStoryMeta((m) => ({ ...m, [key]: sum }));
      if (started) return; // The "Describe it" box only exists before generation.
      const descText = htmlToText(f.description || '');
      const block = [sum, descText].filter(Boolean).join('\n\n');
      if (!block.trim()) return;
      setDescribe((prev) => {
        const base = prev && prev.trim() ? prev.trim() : '';
        if (base && base.includes(block.trim())) return prev; // avoid duplicate seed
        return base ? `${base}\n\n---\n\n${block}` : block;
      });
      setStoryMsg(`Linked ${key} — added its description to “Describe it”.`);
    } catch { /* best-effort seed */ }
  };
  const removeStory = (key) => {
    const k = normKey(key);
    setStoryMsg(null);
    persistLinkedStories(linkedStories.filter((x) => x !== k));
  };
  // Flow-level Stories (from the STORY step) not yet linked to this mockup —
  // offered as one-click "quick add" chips.
  const flowStories = (flow?.linkedTickets?.stories || []).filter((k) => !linkedStories.includes(k));

  // Load a fresh document into the live editor (resets the iframe once).
  const loadDocument = useCallback((newHtml) => {
    setIframeReady(false);
    setSelected(null);
    setHtml(newHtml);
    setSrcDoc(wrapWithEditor(newHtml));
    setStarted(true);
  }, []);

  const startBlank = () => loadDocument(BLANK_HTML);
  const startTemplate = (key) => { if (TEMPLATES[key]) loadDocument(TEMPLATES[key].html); };

  // Read a reference screenshot into a base64 data URL for the AI to match.
  const REF_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
  const handleRefImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageError(null);
    if (!REF_IMAGE_TYPES.includes(file.type)) {
      setImageError('Use a PNG, JPEG, WebP, or GIF image.');
      if (imageInputRef.current) imageInputRef.current.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setImageError('Image is too large (max 10 MB).');
      if (imageInputRef.current) imageInputRef.current.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setRefImage({ name: file.name, dataUrl: reader.result });
    reader.onerror = () => {
      setImageError('Could not read that image.');
      // Reset the input so re-selecting the same file fires onChange again.
      if (imageInputRef.current) imageInputRef.current.value = '';
    };
    reader.readAsDataURL(file);
  };

  const clearRefImage = () => {
    setRefImage(null);
    setImageError(null);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  // Drag the handle below the canvas to resize the preview vertically. Listens
  // on window during the drag so the pointer can leave the small handle.
  const PREVIEW_MIN_H = 240;
  const PREVIEW_MAX_H = 2000;
  const startResize = (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = previewHeight;
    const onMove = (ev) => {
      const next = Math.min(PREVIEW_MAX_H, Math.max(PREVIEW_MIN_H, startH + (ev.clientY - startY)));
      setPreviewHeight(next);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('blur', onUp);
      document.body.style.userSelect = '';
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    // Releasing the pointer outside the window (or losing focus) never fires
    // mouseup, so also stop on blur to avoid leaking listeners / stuck state.
    window.addEventListener('blur', onUp);
  };

  // Keyboard fallback for the resize handle (ArrowUp/ArrowDown; Shift = larger
  // step) so it's operable without a pointer.
  const nudgePreviewHeight = (e) => {
    const step = e.shiftKey ? 40 : 10;
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setPreviewHeight((h) => Math.max(PREVIEW_MIN_H, h - step));
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setPreviewHeight((h) => Math.min(PREVIEW_MAX_H, h + step));
    }
  };

  const generate = async () => {
    if (!describe.trim() && !refImage) return;
    setGenerating(true);
    setError(null);
    setStatus(null);
    try {
      const res = await authenticatedFetch(`${apiUrl}/api/ai/mockup/generate`, {
        method: 'POST',
        body: JSON.stringify({
          description: describe,
          context,
          device,
          selectedModel,
          image: refImage?.dataUrl || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Generation failed');
      setModelUsed(data.modelUsed || null);
      loadDocument(data.html);
    } catch (err) {
      setError(err.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  // Apply an instant op, or fall back to an AI edit of the whole document.
  const runCommand = async () => {
    const text = command.trim();
    if (!text) return;
    const op = parseCommand(text);
    if (op) {
      // Style/layout ops need a target element; delete/duplicate/move too.
      if (!selected) {
        setError('Select an element in the preview first, then run the command.');
        return;
      }
      setError(null);
      postToIframe({ type: 'apply', op });
      setCommand('');
      return;
    }
    await runAiEdit(text);
  };

  const runAiEdit = async (instruction) => {
    if (!html) return;
    setEditing(true);
    setError(null);
    setStatus(null);
    try {
      const res = await authenticatedFetch(`${apiUrl}/api/ai/mockup/edit`, {
        method: 'POST',
        body: JSON.stringify({ html, instruction, selectedElement: selected?.outer || null, selectedModel }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Edit failed');
      setModelUsed(data.modelUsed || null);
      loadDocument(data.html);
      setCommand('');
    } catch (err) {
      setError(err.message || 'Edit failed');
    } finally {
      setEditing(false);
    }
  };

  // Direct instant ops (buttons). Style/layout ops need a selection.
  const doOp = (op) => {
    if (!iframeReady) return;
    if (op.kind !== 'delete' && op.kind !== 'duplicate' && !selected) {
      setError('Select an element in the preview first.');
      return;
    }
    setError(null);
    postToIframe({ type: 'apply', op });
  };

  // Insert a palette element into the canvas (after the selected element if any,
  // else appended). No selection is required.
  const insertElement = (html) => {
    if (!iframeReady) return;
    setError(null);
    postToIframe({ type: 'apply', op: { kind: 'insert', html } });
  };

  const save = async (markComplete) => {
    if (!onSave) return;
    setSaving(true);
    setError(null);
    setStatus(null);
    try {
      await onSave(docType, {
        html,
        phase: 'static',
        status: markComplete ? 'complete' : 'in_progress',
        modelUsed,
      });
      setStatus(markComplete ? 'Saved & marked complete.' : 'Draft saved.');
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Return to the start screen and reset this step to "Not started". If a mockup
  // was already saved on the flow, clear it there too so the rail badge resets.
  const resetMockup = async () => {
    setConfirmOpen(false);
    const hasSaved = Boolean(existing?.html) || (existing?.status && existing.status !== 'not_started');
    setSelected(null);
    setHtml('');
    setSrcDoc('');
    setModelUsed(null);
    setStarted(false);
    setError(null);
    setStatus(null);
    setCommand('');
    if (onSave && hasSaved) {
      try {
        await onSave(docType, { html: '', phase: 'static', status: 'not_started', modelUsed: null });
      } catch (err) {
        setError(err.message || 'Reset failed');
      }
    }
  };

  const startOver = () => {
    const hasSaved = Boolean(existing?.html) || (existing?.status && existing.status !== 'not_started');
    if (html || hasSaved) { setConfirmOpen(true); return; }
    resetMockup();
  };

  // Open the "Push to Jira" modal, pre-selecting all linked stories.
  const openPush = () => {
    setPushResults(null);
    setPushError(null);
    setPushSel(linkedStories);
    setPushImage(true);
    setPushLink(true);
    setPushOpen(true);
  };

  const togglePushKey = (k) =>
    setPushSel((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  // Attach the current mockup (image + link) to the selected Jira stories.
  const runPush = async () => {
    if (!pushSel.length) { setPushError('Select at least one story.'); return; }
    if (!pushImage && !pushLink) { setPushError('Choose at least one: attach an image or add a link.'); return; }
    if (!flow?.id) { setPushError('Save the flow first.'); return; }
    setPushing(true);
    setPushError(null);
    setPushResults(null);
    try {
      const res = await authenticatedFetch(`${apiUrl}/api/qa/flows/${flow.id}/mockup/push-to-jira`, {
        method: 'POST',
        body: JSON.stringify({ storyKeys: pushSel, includeImage: pushImage, includeLink: pushLink, html, device }),
      });
      const data = await res.json();
      if (data.results) setPushResults(data.results);
      if (!res.ok || !data.success) {
        setPushError(data.error || (data.results ? 'Some stories failed — see below.' : 'Push failed.'));
      }
    } catch (err) {
      setPushError(err.message || 'Push failed');
    } finally {
      setPushing(false);
    }
  };

  // Counts of what "Build this app" will read from the flow.
  // Counts of what "Build this app" will read from the flow.
  const buildEpicKeys = (flow?.linkedTickets?.epics) || [];
  const buildStoryKeys = (flow?.linkedTickets?.stories) || [];
  const draftStoryCount = Object.values(flow?.storyDrafts || {})
    .reduce((n, arr) => n + (Array.isArray(arr) ? arr.length : 0), 0);

  // Open the "Build this app" modal, seeding name/repo from the flow.
  const openBuild = () => {
    setBuildResult(null);
    setBuildError(null);
    setBuildTokenSetup(null);
    setBuildAppName(flow?.name || '');
    setBuildRepoName(toRepoSlug(flow?.name || ''));
    setBuildOpen(true);
  };

  // Switch platform and reset the framework to that platform's default.
  const pickPlatform = (p) => {
    setBuildPlatform(p);
    setBuildFramework((BUILD_FRAMEWORKS[p] || [])[0]?.id || 'node');
  };

  // Provision a repo + AI-scaffold a starter app from the Epic/Stories/mockup.
  const runBuild = async (overrideToken) => {
    if (!flow?.id) { setBuildError('Save the flow first.'); return; }
    setBuilding(true);
    setBuildError(null);
    setBuildResult(null);
    try {
      const tokenToSend = (typeof overrideToken === 'string' ? overrideToken : githubToken || '').trim();
      const headers = {};
      if (tokenToSend) headers['X-User-GitHub-Token'] = tokenToSend;
      const res = await authenticatedFetch(`${apiUrl}/api/deploy/build-from-flow`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          flowId: flow.id,
          platform: buildPlatform,
          framework: buildFramework,
          appName: buildAppName.trim() || flow.name,
          repoName: buildRepoName.trim(),
          selectedModel,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok && data?.requiresGitHubToken) {
        setBuildTokenSetup(data.tokenSetup || null);
        setBuildError(data.error || 'A GitHub Personal Access Token is required.');
        return;
      }
      if (!res.ok && data?.status !== 'repo_created_scaffold_failed') {
        setBuildError(data?.error || 'Build failed.');
        return;
      }
      if (tokenToSend && rememberToken) {
        try { localStorage.setItem(GITHUB_TOKEN_STORAGE_KEY, tokenToSend); } catch { /* ignore */ }
      }
      setBuildTokenSetup(null);
      setBuildResult(data);
    } catch (err) {
      setBuildError(err.message || 'Build failed');
    } finally {
      setBuilding(false);
    }
  };

  const busy = generating || editing;

  return (
    <div style={s.wrap}>
      <LyricConfirm
        isOpen={confirmOpen}
        title="Start over?"
        message="This clears the current mockup and resets this step to “Not started”. This can’t be undone."
        confirmLabel="Start over"
        cancelLabel="Cancel"
        tone="danger"
        onConfirm={resetMockup}
        onCancel={() => setConfirmOpen(false)}
      />
      {pushOpen && (
        <div style={s.pushOverlay} onClick={() => !pushing && setPushOpen(false)}>
          <div style={s.pushModal} onClick={(e) => e.stopPropagation()}>
            <div style={s.pushTitle}>📤 Push mockup to Jira</div>
            <p style={s.pushLead}>Attach this mockup to the selected Jira Stories so the team can see it right in the ticket.</p>

            <div style={s.pushSectionLabel}>Stories</div>
            <div style={s.pushStories}>
              {linkedStories.length === 0 ? (
                <p style={s.storyEmpty}>No linked stories.</p>
              ) : linkedStories.map((k) => (
                <label key={k} style={s.pushStoryRow}>
                  <input type="checkbox" checked={pushSel.includes(k)} onChange={() => togglePushKey(k)} />
                  <strong>{k}</strong>
                  {storyMeta[k] ? <span style={s.chipSummary}>{storyMeta[k]}</span> : null}
                </label>
              ))}
            </div>

            <div style={s.pushSectionLabel}>What to add</div>
            <label style={s.pushOpt}>
              <input type="checkbox" checked={pushImage} onChange={(e) => setPushImage(e.target.checked)} />
              <span>Attach a <strong>PNG image</strong> — renders inline in the ticket</span>
            </label>
            <label style={s.pushOpt}>
              <input type="checkbox" checked={pushLink} onChange={(e) => setPushLink(e.target.checked)} />
              <span>Add a <strong>clickable link</strong> (comment + web link) to the live mockup</span>
            </label>

            {pushError && <div style={s.pushErr}>{pushError}</div>}
            {pushResults && (
              <div style={s.pushResults}>
                {pushResults.map((r) => (
                  <div key={r.key} style={s.pushResultRow}>
                    <strong>{r.key}</strong>{' '}
                    {r.error ? (
                      <span style={s.pushBad}>✕ {r.error}</span>
                    ) : (
                      <span style={s.pushGood}>
                        ✓ {[r.attached && 'image', r.commented && 'link', r.linked && 'web link'].filter(Boolean).join(' + ') || 'nothing'} added
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div style={s.pushActions}>
              <button style={s.pushCancel} onClick={() => setPushOpen(false)} disabled={pushing}>
                {pushResults ? 'Close' : 'Cancel'}
              </button>
              <button style={{ ...s.pushGo, ...(pushing ? s.btnDisabled : {}) }} onClick={runPush} disabled={pushing}>
                {pushing ? 'Pushing…' : (pushResults ? 'Push again' : 'Push to Jira')}
              </button>
            </div>
          </div>
        </div>
      )}
      {buildOpen && (
        <div style={s.pushOverlay} onClick={() => !building && setBuildOpen(false)}>
          <div style={s.pushModal} onClick={(e) => e.stopPropagation()}>
            <div style={s.pushTitle}>🏗️ Build this app</div>
            <p style={s.pushLead}>
              Reads this flow’s Epic, its Stories and this mockup, provisions a private GitHub repo,
              and AI-scaffolds a starter app you can then deploy from the <strong>Deploy App</strong> tab.
            </p>

            <div style={s.buildSpec}>
              Will read: <strong>{buildEpicKeys.length}</strong> epic{buildEpicKeys.length === 1 ? '' : 's'} ·{' '}
              <strong>{buildStoryKeys.length}</strong> Jira stor{buildStoryKeys.length === 1 ? 'y' : 'ies'}
              {draftStoryCount > 0 ? <> · <strong>{draftStoryCount}</strong> draft{draftStoryCount === 1 ? '' : 's'}</> : null}
              {' '}· this mockup as the design reference.
            </div>

            <div style={s.buildLabel}>Platform</div>
            <div style={s.buildPlatforms}>
              {BUILD_PLATFORMS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  style={{ ...s.buildChoice, ...(buildPlatform === p.id ? s.buildChoiceOn : {}) }}
                  onClick={() => pickPlatform(p.id)}
                  disabled={building}
                >{p.label}</button>
              ))}
            </div>

            <div style={s.buildLabel}>Framework / language</div>
            <select
              style={s.buildInput}
              value={buildFramework}
              onChange={(e) => setBuildFramework(e.target.value)}
              disabled={building}
            >
              {(BUILD_FRAMEWORKS[buildPlatform] || []).map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
            <p style={s.buildNote}>
              {buildPlatform === 'web'
                ? 'Web apps are generated to be deployable — test them from the Deploy App tab.'
                : 'Mobile apps are generated as a code scaffold to open in Xcode / Android Studio (not deployable via the web Deploy flow).'}
            </p>

            <div style={s.buildLabel}>App name</div>
            <input
              style={s.buildInput}
              value={buildAppName}
              onChange={(e) => setBuildAppName(e.target.value)}
              onBlur={() => { if (!buildRepoName.trim()) setBuildRepoName(toRepoSlug(buildAppName)); }}
              placeholder="My App"
              disabled={building}
            />

            <div style={s.buildLabel}>Repository name (ischemaview/…)</div>
            <input
              style={s.buildInput}
              value={buildRepoName}
              onChange={(e) => setBuildRepoName(toRepoSlug(e.target.value))}
              placeholder="my-app"
              disabled={building}
            />

            {(buildTokenSetup || githubToken) && (
              <div style={s.tokenBox}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>GitHub Personal Access Token</div>
                <div style={{ marginBottom: 6 }}>
                  Needed to create the repo under <strong>ischemaview</strong>.{' '}
                  {buildTokenSetup?.createTokenUrl && (
                    <a href={buildTokenSetup.createTokenUrl} target="_blank" rel="noreferrer" style={s.tokenLink}>Create one ↗</a>
                  )}
                </div>
                <input
                  style={s.buildInput}
                  type="password"
                  value={githubToken}
                  onChange={(e) => setGithubToken(e.target.value)}
                  placeholder="ghp_…"
                  disabled={building}
                />
                <label style={{ ...s.pushOpt, fontSize: 12 }}>
                  <input type="checkbox" checked={rememberToken} onChange={(e) => setRememberToken(e.target.checked)} />
                  <span>Remember this token in my browser (localStorage only)</span>
                </label>
              </div>
            )}

            {buildError && <div style={s.pushErr}>{buildError}</div>}

            {buildResult && buildResult.repoUrl && (
              <div style={s.buildOk}>
                <div style={s.buildOkTitle}>
                  {buildResult.status === 'built' ? '✅ App scaffolded!' : '⚠️ Repo created (scaffold incomplete)'}
                </div>
                {buildResult.summary && <div>{buildResult.summary}</div>}
                <a href={buildResult.repoUrl} target="_blank" rel="noreferrer" style={s.buildLink}>{buildResult.repoUrl} ↗</a>
                {typeof buildResult.filesCreated === 'number' && (
                  <div style={s.buildMeta}>
                    {buildResult.filesCreated} files · {buildResult.platform}/{buildResult.framework}
                    {buildResult.filesFailed ? ` · ${buildResult.filesFailed} failed` : ''}
                    {buildResult.modelUsed ? ` · ${buildResult.modelUsed}` : ''}
                  </div>
                )}
                {buildResult.error && <div style={{ ...s.pushErr, marginTop: 8 }}>{buildResult.error}</div>}
                {buildResult.status === 'built' && buildResult.platform === 'web' && (
                  <div style={s.buildMeta}>Next: open the <strong>Deploy App</strong> tab to deploy &amp; test.</div>
                )}
              </div>
            )}

            <div style={s.pushActions}>
              <button style={s.pushCancel} onClick={() => setBuildOpen(false)} disabled={building}>
                {buildResult ? 'Close' : 'Cancel'}
              </button>
              {!buildResult && (
                <button
                  style={{ ...s.buildGo, ...(building ? s.btnDisabled : {}) }}
                  onClick={() => runBuild()}
                  disabled={building}
                >
                  {building ? 'Building… (this can take a minute)' : (buildTokenSetup ? 'Enter token & build' : '🏗️ Build app')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <div style={s.phaseNote}>
        Phase 1 · <strong>Static visual</strong> mockup. Click an element to select it, then use the command bar
        (e.g. <em>“move it down 10”, “make it blue”, “bigger”, “duplicate”</em>) for instant edits, or describe a
        structural change to let AI rewrite it. Later phases add interactivity and “Promote to app”.
      </div>

      {error && <div style={s.error}>{error}</div>}
      {status && <div style={s.okNote}>{status}</div>}

      {/* Linked Stories — pick the Jira Stories this mockup is designed for.
          Hidden when embedded in the Build-from-Scratch deploy flow. */}
      {!embedded && (
      <div style={s.storyBox}>
        <div
          style={s.storyHead}
          onClick={() => setStoriesCollapsed((v) => !v)}
          title={storiesCollapsed ? 'Expand linked stories' : 'Collapse linked stories'}
          role="button"
        >
          <span style={s.storyToggle}>{storiesCollapsed ? '▸' : '▾'}</span>
          🔗 Linked Stories
          {linkedStories.length > 0 && <span style={s.storyCount}>{linkedStories.length}</span>}
          {!storiesCollapsed && (
            <span style={s.storyHeadHint}>Select the Jira Stories this mockup is designed for.</span>
          )}
        </div>

        {storiesCollapsed ? (
          linkedStories.length > 0 ? (
            <div style={s.chipWrapCollapsed}>
              {linkedStories.map((k) => (
                <span key={k} style={s.storyChipMini} onClick={(e) => e.stopPropagation()}>
                  <JiraTicketLink ticketKey={k} />
                </span>
              ))}
            </div>
          ) : null
        ) : (
          <>
            {linkedStories.length > 0 ? (
              <div style={s.chipWrap}>
                {linkedStories.map((k) => (
                  <span key={k} style={s.storyChip}>
                    <JiraTicketLink ticketKey={k} />
                    {storyMeta[k] ? <span style={s.chipSummary}>{storyMeta[k]}</span> : null}
                    <button
                      type="button"
                      style={s.chipRemove}
                      title={`Unlink ${k}`}
                      onClick={() => removeStory(k)}
                    >✕</button>
                  </span>
                ))}
              </div>
            ) : (
              <p style={s.storyEmpty}>No stories linked yet.</p>
            )}

            {flowStories.length > 0 && (
              <div style={s.quickAddRow}>
                <span style={s.quickAddLabel}>Stories in this flow:</span>
                {flowStories.map((k) => (
                  <button key={k} type="button" style={s.quickAddChip} onClick={() => addStory(k)}>
                    + {k}
                  </button>
                ))}
              </div>
            )}

            {selectedProject ? (
              <div style={s.storySearchWrap}>
                <input
                  style={s.storyInput}
                  placeholder={`🔍 Search Stories in ${selectedProject} by key or summary…`}
                  value={storySearch}
                  onChange={(e) => setStorySearch(e.target.value)}
                />
                {storySearch.trim().length >= 2 && (
                  <div style={s.storyResults}>
                    {storySearching ? (
                      <div style={s.storyResultHint}>⏳ Searching…</div>
                    ) : storyResults.filter((r) => !linkedStories.includes(r.key)).length === 0 ? (
                      <div style={s.storyResultHint}>No matching stories.</div>
                    ) : (
                      storyResults
                        .filter((r) => !linkedStories.includes(r.key))
                        .slice(0, 20)
                        .map((r) => (
                          <button
                            key={r.key}
                            type="button"
                            style={s.storyResultBtn}
                            onClick={() => addStory(r.key, r.fields?.summary || '')}
                          >
                            <strong>{r.key}</strong> — {r.fields?.summary || ''}
                            {r.fields?.status?.name ? <span style={s.storyStatus}>{r.fields.status.name}</span> : null}
                          </button>
                        ))
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p style={s.storyEmpty}>Select a project at the top of the page to search for stories.</p>
            )}
            {storyMsg && <div style={s.storyMsg}>{storyMsg}</div>}
          </>
        )}
      </div>
      )}

      {!started ? (
        <div style={s.startGrid}>
          <div style={s.startCard}>
            <div style={s.startHead}>{embedded ? '🖊️ Generate from your app description' : '🖊️ Describe it'}</div>
            <p style={s.startSub}>
              {embedded
                ? 'AI will build the first draft from the app description you already entered. Optionally attach a reference screenshot to match its layout & style.'
                : 'Describe the app or page and/or attach a reference screenshot, and let AI build the first draft.'}
            </p>
            {!embedded && (
              <textarea
                style={s.textarea}
                rows={3}
                placeholder="e.g. A dashboard for stroke workflow metrics with a sidebar, 3 KPI cards and a recent-cases table"
                value={describe}
                onChange={(e) => setDescribe(e.target.value)}
              />
            )}
            <label style={s.ctxLabel} htmlFor="mockup-ref-image">Reference image (optional — AI matches its layout & style)</label>
            {refImage ? (
              <div style={s.refImageRow}>
                <img src={refImage.dataUrl} alt="Reference preview" style={s.refImageThumb} />
                <span style={s.refImageName} title={refImage.name}>{refImage.name}</span>
                <button type="button" style={s.linkBtn} onClick={clearRefImage}>remove</button>
              </div>
            ) : (
              <input
                ref={imageInputRef}
                id="mockup-ref-image"
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={handleRefImageUpload}
                style={s.fileInput}
              />
            )}
            {imageError && <div style={s.imageError}>⚠️ {imageError}</div>}
            <label style={s.ctxLabel}>Context for AI (from the flow — editable)</label>
            <textarea style={s.textareaSm} rows={2} value={context} onChange={(e) => setContext(e.target.value)} />
            <div style={s.deviceRow}>
              <span style={s.deviceLabel}>Target:</span>
              <button style={{ ...s.chipBtn, ...(device === 'desktop' ? s.chipBtnActive : {}) }} onClick={() => setDevice('desktop')}>🖥 Desktop</button>
              <button style={{ ...s.chipBtn, ...(device === 'mobile' ? s.chipBtnActive : {}) }} onClick={() => setDevice('mobile')}>📱 Mobile</button>
            </div>
            <button style={{ ...s.primaryBtn, ...(generating || (!describe.trim() && !refImage) ? s.btnDisabled : {}) }} onClick={generate} disabled={generating || (!describe.trim() && !refImage)}>
              {generating ? '✨ Generating…' : '✨ Generate mockup'}
            </button>
          </div>

          <div style={s.startCard}>
            <div style={s.startHead}>🧩 Start from a template</div>
            <p style={s.startSub}>Pick a starting layout and edit from there.</p>
            <div style={s.tplGrid}>
              {Object.entries(TEMPLATES).map(([key, t]) => (
                <button key={key} style={s.tplBtn} onClick={() => startTemplate(key)}>{t.label}</button>
              ))}
            </div>
            <div style={{ ...s.startHead, marginTop: 18 }}>⬜ Blank canvas</div>
            <p style={s.startSub}>Start empty and build up with commands + AI.</p>
            <button style={s.secondaryBtn} onClick={startBlank}>Start blank</button>
          </div>
        </div>
      ) : (
        <>
          {/* Toolbar */}
          <div style={s.toolbar}>
            <div style={s.toolGroup}>
              <span style={s.toolLabel}>Preview:</span>
              <button style={{ ...s.chipBtn, ...(device === 'desktop' ? s.chipBtnActive : {}) }} onClick={() => setDevice('desktop')}>🖥 Desktop</button>
              <button style={{ ...s.chipBtn, ...(device === 'mobile' ? s.chipBtnActive : {}) }} onClick={() => setDevice('mobile')}>📱 Mobile</button>
            </div>
            <div style={s.toolGroup}>
              <button style={s.secondaryBtn} onClick={startOver}>↺ Start over</button>
            </div>
          </div>

          {/* Command bar */}
          <div style={s.cmdBar}>
            <input
              style={s.cmdInput}
              placeholder='Command… e.g. "move it down 10", "make it blue", "add a login button", "bigger"'
              value={command}
              onChange={(e) => setCommand(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') runCommand(); }}
              disabled={busy}
            />
            <button style={{ ...s.primaryBtn, ...(busy || !command.trim() ? s.btnDisabled : {}) }} onClick={runCommand} disabled={busy || !command.trim()}>
              {editing ? '✨ Editing…' : 'Apply'}
            </button>
          </div>
          <p style={s.cmdHint}>
            Simple layout/style tweaks apply instantly. Anything structural (“add a…”, “rearrange…”, “change the layout”) is handled by AI{modelUsed ? ` · last model: ${modelUsed}` : ''}.
          </p>

          {/* Live preview */}
          <div style={{ ...s.previewOuter, ...(embedded ? { paddingLeft: 192 } : {}) }}>
            <div style={{ ...s.previewCol, width: device === 'mobile' ? 390 : '100%' }}>
              <div style={{ ...(device === 'mobile' ? s.previewMobile : s.previewDesktop), height: previewHeight }}>
                <iframe
                  ref={iframeRef}
                  title="Mockup preview"
                  srcDoc={srcDoc}
                  style={s.iframe}
                  sandbox="allow-scripts allow-same-origin"
                />
              </div>
              <div
                style={s.resizeHandle}
                onMouseDown={startResize}
                onKeyDown={nudgePreviewHeight}
                title="Drag (or focus and use ↑/↓) to resize the preview height"
                role="separator"
                aria-orientation="horizontal"
                aria-label="Resize preview height"
                aria-valuenow={previewHeight}
                aria-valuemin={PREVIEW_MIN_H}
                aria-valuemax={PREVIEW_MAX_H}
                tabIndex={0}
              >
                <span style={s.resizeGrip}>⋯ drag to resize ⋯</span>
              </div>
            </div>

            {/* Quick-insert palette — docked to the left of the canvas area and
                scrolls with the page. Click an element to drop it into the canvas
                (after the selected element, or at the end). */}
            <div style={{ ...s.palette, ...(embedded ? { right: 'auto', left: 12 } : {}) }}>
              <div style={s.paletteHead}>
                <span style={s.paletteTitle}>Add elements</span>
                <button
                  style={s.paletteMinBtn}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => setPaletteMin((v) => !v)}
                  title={paletteMin ? 'Expand' : 'Minimize'}
                >{paletteMin ? '▢' : '—'}</button>
              </div>
              {!paletteMin && (
                <div style={s.paletteGrid}>
                  {PALETTE_ELEMENTS.map((el) => (
                    <button
                      key={el.label}
                      style={s.paletteBtn}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() => insertElement(el.html)}
                      title={`Insert ${el.label}`}
                    >
                      <span style={s.paletteIcon}>{el.icon}</span>
                      <span>{el.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Selection + inspector live BELOW the canvas, so selecting or
              deselecting an element toggles panels here instead of pushing the
              canvas up and down. */}
          <div style={s.selBar}>
            {selected ? (
              <span style={s.selInfo}>
                Selected: <strong>&lt;{selected.tag}&gt;</strong>{selected.text ? ` — “${selected.text.slice(0, 40)}”` : ''}
                <button style={s.linkBtn} onClick={() => { postToIframe({ type: 'deselect' }); }}>clear</button>
              </span>
            ) : (
              <span style={s.selHint}>Click to select · drag to move · double-click to edit text.</span>
            )}
            <div style={s.quickOps}>
              <button style={s.opBtn} disabled={!iframeReady} onClick={() => doOp({ kind: 'move', dy: -10 })}>↑</button>
              <button style={s.opBtn} disabled={!iframeReady} onClick={() => doOp({ kind: 'move', dy: 10 })}>↓</button>
              <button style={s.opBtn} disabled={!iframeReady} onClick={() => doOp({ kind: 'move', dx: -10 })}>←</button>
              <button style={s.opBtn} disabled={!iframeReady} onClick={() => doOp({ kind: 'move', dx: 10 })}>→</button>
              <button style={s.opBtn} disabled={!iframeReady} onClick={() => doOp({ kind: 'duplicate' })}>⧉ Dup</button>
              <button style={{ ...s.opBtn, color: '#b91c1c' }} disabled={!iframeReady} onClick={() => doOp({ kind: 'delete' })}>🗑</button>
            </div>
          </div>

          {/* Live inspector — instant edits for the selected element */}
          {selected && (
            <div style={s.inspector}>
              <div style={s.inspHead}>Edit &lt;{selected.tag}&gt; — changes apply live</div>
              <div style={s.inspGrid}>
                {selected.childCount === 0 && (
                  <label style={s.inspField}>
                    <span style={s.inspLabel}>Text</span>
                    <input
                      style={s.inspText}
                      value={textDraft}
                      onChange={(e) => { setTextDraft(e.target.value); doOp({ kind: 'setText', value: e.target.value }); }}
                      placeholder="(element text)"
                    />
                  </label>
                )}
                <label style={s.inspField}>
                  <span style={s.inspLabel}>Text color</span>
                  <span style={s.inspSwatchRow}>
                    <input type="color" style={s.inspColor} value={colorDraft}
                      onChange={(e) => { setColorDraft(e.target.value); doOp({ kind: 'color', value: e.target.value }); }} />
                    <span style={s.inspVal}>{colorDraft}</span>
                  </span>
                </label>
                <label style={s.inspField}>
                  <span style={s.inspLabel}>Background</span>
                  <span style={s.inspSwatchRow}>
                    <input type="color" style={s.inspColor} value={bgDraft}
                      onChange={(e) => { setBgDraft(e.target.value); doOp({ kind: 'background', value: e.target.value }); }} />
                    <button style={s.inspMini} onClick={() => { setBgDraft('#ffffff'); doOp({ kind: 'background', value: 'transparent' }); }}>clear</button>
                  </span>
                </label>
                <label style={s.inspField}>
                  <span style={s.inspLabel}>Font size · {fontDraft}px</span>
                  <input type="range" min={8} max={72} step={1} style={s.inspRange} value={fontDraft}
                    onChange={(e) => { const v = Number(e.target.value); setFontDraft(v); doOp({ kind: 'fontSizeAbs', value: v }); }} />
                </label>
                <div style={s.inspField}>
                  <span style={s.inspLabel}>Style</span>
                  <span style={s.inspBtnRow}>
                    <button
                      style={{ ...s.inspToggle, ...(boldOn ? s.inspToggleOn : {}) }}
                      onClick={() => { const nv = !boldOn; setBoldOn(nv); doOp({ kind: 'fontWeight', value: nv ? 'bold' : 'normal' }); }}
                    >B</button>
                    <button style={s.inspMini} onClick={() => { const v = Math.min(72, fontDraft + 2); setFontDraft(v); doOp({ kind: 'fontSizeAbs', value: v }); }}>A+</button>
                    <button style={s.inspMini} onClick={() => { const v = Math.max(8, fontDraft - 2); setFontDraft(v); doOp({ kind: 'fontSizeAbs', value: v }); }}>A−</button>
                  </span>
                </div>
                <div style={s.inspField}>
                  <span style={s.inspLabel}>Align</span>
                  <span style={s.inspBtnRow}>
                    {['left', 'center', 'right'].map((a) => (
                      <button key={a}
                        style={{ ...s.inspMini, ...(alignVal === a ? s.inspToggleOn : {}) }}
                        onClick={() => { setAlignVal(a); doOp({ kind: 'align', value: a }); }}
                      >{a === 'left' ? '⯇' : a === 'center' ? '☰' : '⯈'}</button>
                    ))}
                  </span>
                </div>
              </div>
              <p style={s.inspHint}>Tip: double-click any element in the preview to edit its text right on the canvas.</p>
            </div>
          )}

          {/* Save */}
          <div style={s.saveRow}>
            {embedded ? (
              <button style={{ ...s.saveBtnPrimary, ...((saving || !html) ? s.btnDisabled : {}) }} onClick={() => save(false)} disabled={saving || !html}>
                💾 Save mockup for this app
              </button>
            ) : (
              <>
                <button style={{ ...s.saveBtn, ...(saving ? s.btnDisabled : {}) }} onClick={() => save(false)} disabled={saving}>
                  💾 Save draft
                </button>
                <button style={{ ...s.saveBtnPrimary, ...(saving ? s.btnDisabled : {}) }} onClick={() => save(true)} disabled={saving}>
                  ✅ Save & mark complete
                </button>
                <button
                  style={{ ...s.buildBtn, ...((building || !html) ? s.btnDisabled : {}) }}
                  onClick={openBuild}
                  disabled={building || !html}
                  title={html ? 'Generate a starter app repo from this Epic + Stories + mockup' : 'Build or generate the mockup first'}
                >
                  🏗️ Build this app (Beta)
                </button>
                <button
                  style={{ ...s.pushBtn, ...((pushing || linkedStories.length === 0) ? s.btnDisabled : {}) }}
                  onClick={openPush}
                  disabled={pushing || linkedStories.length === 0}
                  title={linkedStories.length ? 'Attach this mockup to its linked Jira stories' : 'Link a Jira story first (see Linked Stories above)'}
                >
                  📤 Push to Jira
                </button>
              </>
            )}
            {saving && <span style={s.help}>Saving…</span>}
            {!embedded && linkedStories.length === 0 && <span style={s.help}>Link a story above to enable “Push to Jira”.</span>}
            {embedded && !saving && <span style={s.help}>Saved to this app — the AI builder will use it as the UI design reference.</span>}
          </div>
        </>
      )}
    </div>
  );
};

const s = {
  wrap: {},
  title: { fontSize: 18, margin: '0 0 6px', color: '#222' },
  help: { color: '#777', fontSize: 12, margin: '6px 0 0' },
  phaseNote: { background: '#f5f8ff', border: '1px solid #dbe4ff', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#333', margin: '12px 0' },
  storyBox: { border: '1px solid #e3e3e3', borderRadius: 8, padding: '10px 12px', margin: '12px 0', background: '#fafafa' },
  storyHead: { fontSize: 14, fontWeight: 700, color: '#222', display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', cursor: 'pointer', userSelect: 'none' },
  storyToggle: { fontSize: 12, color: '#6b7280', width: 12, display: 'inline-block' },
  storyCount: { fontSize: 12, fontWeight: 700, color: '#3730a3', background: '#e0e7ff', borderRadius: 10, padding: '1px 8px' },
  chipWrapCollapsed: { display: 'flex', flexWrap: 'wrap', gap: 6, margin: '8px 0 0' },
  storyChipMini: { display: 'inline-flex', alignItems: 'center', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 12, padding: '2px 8px', fontSize: 12 },
  storyHeadHint: { fontSize: 12, fontWeight: 400, color: '#777' },
  storyEmpty: { fontSize: 13, color: '#888', margin: '8px 0 0' },
  chipWrap: { display: 'flex', flexWrap: 'wrap', gap: 8, margin: '10px 0 0' },
  storyChip: { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 16, padding: '4px 8px 4px 10px', fontSize: 13 },
  chipSummary: { color: '#555', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  chipRemove: { background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: '0 2px' },
  quickAddRow: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, margin: '10px 0 0' },
  quickAddLabel: { fontSize: 12, color: '#666', marginRight: 2 },
  quickAddChip: { background: '#fff', border: '1px dashed #9ca3af', borderRadius: 16, padding: '3px 10px', fontSize: 12, cursor: 'pointer', color: '#374151' },
  storySearchWrap: { position: 'relative', marginTop: 10 },
  storyInput: { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' },
  storyResults: { position: 'absolute', left: 0, right: 0, top: '100%', zIndex: 30, border: '1px solid #e3e3e3', borderRadius: 6, marginTop: 6, background: '#fff', maxHeight: 260, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,.12)' },
  storyResultHint: { padding: '8px 10px', fontSize: 13, color: '#888' },
  storyResultBtn: { display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderBottom: '1px solid #f0f0f0', padding: '8px 10px', fontSize: 13, cursor: 'pointer', color: '#333' },
  storyStatus: { marginLeft: 8, fontSize: 11, color: '#6b7280', background: '#f3f4f6', borderRadius: 10, padding: '1px 8px' },
  storyMsg: { fontSize: 12, color: '#047857', marginTop: 8 },
  error: { background: '#fde8e8', color: '#9b1c1c', padding: '10px 12px', borderRadius: 6, margin: '10px 0', fontSize: 14 },
  okNote: { background: '#ecfdf5', color: '#047857', padding: '10px 12px', borderRadius: 6, margin: '10px 0', fontSize: 14, fontWeight: 600 },
  startGrid: { display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8 },
  startCard: { flex: '1 1 320px', border: '1px solid #e3e3e3', borderRadius: 10, padding: 18, background: '#fafafa' },
  startHead: { fontSize: 15, fontWeight: 700, color: '#222' },
  startSub: { color: '#666', fontSize: 13, margin: '4px 0 10px' },
  textarea: { width: '100%', boxSizing: 'border-box', padding: '9px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', resize: 'vertical' },
  textareaSm: { width: '100%', boxSizing: 'border-box', padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', resize: 'vertical', color: '#444' },
  ctxLabel: { display: 'block', fontSize: 12, fontWeight: 600, color: '#555', margin: '10px 0 4px' },
  fileInput: { display: 'block', fontSize: 13, color: '#444', width: '100%' },
  refImageRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', border: '1px solid #e3e3e3', borderRadius: 8, background: '#fff' },
  refImageThumb: { width: 48, height: 48, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd', flexShrink: 0 },
  refImageName: { fontSize: 13, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 },
  imageError: { color: '#b91c1c', fontSize: 12, marginTop: 6 },
  deviceRow: { display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0' },
  deviceLabel: { fontSize: 13, color: '#555' },
  tplGrid: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  tplBtn: { flex: '1 1 45%', padding: '10px 12px', border: '1px solid #ccc', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#333' },
  primaryBtn: { background: '#1a56db', color: '#fff', border: 'none', padding: '10px 16px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 8 },
  secondaryBtn: { background: '#fff', border: '1px solid #ccc', padding: '8px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#333' },
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  chipBtn: { padding: '6px 12px', border: '1px solid #ccc', borderRadius: 20, background: '#fff', color: '#333', fontSize: 13, cursor: 'pointer' },
  chipBtnActive: { background: '#1a56db', color: '#fff', borderColor: '#1a56db' },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 8 },
  toolGroup: { display: 'flex', alignItems: 'center', gap: 8 },
  toolLabel: { fontSize: 13, color: '#555' },
  selBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, background: '#f3f4f6', border: '1px solid #e3e3e3', borderRadius: 8, padding: '8px 12px', marginTop: 12 },
  selInfo: { fontSize: 13, color: '#222' },
  selHint: { fontSize: 13, color: '#888' },
  linkBtn: { background: 'none', border: 'none', color: '#1a56db', cursor: 'pointer', fontSize: 12, marginLeft: 8, textDecoration: 'underline', padding: 0 },
  quickOps: { display: 'flex', gap: 4, flexWrap: 'wrap' },
  opBtn: { minWidth: 34, padding: '6px 8px', border: '1px solid #ccc', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#333' },
  inspector: { border: '1px solid #dbe4ff', background: '#f8faff', borderRadius: 8, padding: '10px 12px', marginTop: 10 },
  inspHead: { fontSize: 13, fontWeight: 700, color: '#1e40af', marginBottom: 8 },
  inspGrid: { display: 'flex', flexWrap: 'wrap', gap: '10px 18px', alignItems: 'flex-end' },
  inspField: { display: 'flex', flexDirection: 'column', gap: 4 },
  inspLabel: { fontSize: 11, fontWeight: 600, color: '#555', textTransform: 'uppercase', letterSpacing: '.02em' },
  inspText: { padding: '7px 9px', border: '1px solid #ccc', borderRadius: 6, fontSize: 13, minWidth: 220, fontFamily: 'inherit' },
  inspSwatchRow: { display: 'flex', alignItems: 'center', gap: 6 },
  inspColor: { width: 40, height: 30, padding: 0, border: '1px solid #ccc', borderRadius: 6, background: '#fff', cursor: 'pointer' },
  inspVal: { fontSize: 12, color: '#666', fontFamily: 'monospace' },
  inspRange: { width: 150 },
  inspBtnRow: { display: 'flex', gap: 4 },
  inspToggle: { minWidth: 32, padding: '6px 8px', border: '1px solid #ccc', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 800, color: '#333' },
  inspToggleOn: { background: '#1a56db', color: '#fff', borderColor: '#1a56db' },
  inspMini: { minWidth: 32, padding: '6px 8px', border: '1px solid #ccc', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#333' },
  inspHint: { fontSize: 12, color: '#888', margin: '8px 0 0' },
  cmdBar: { display: 'flex', gap: 8, marginTop: 12 },
  cmdInput: { flex: 1, padding: '10px 12px', border: '1px solid #ccc', borderRadius: 8, fontSize: 14 },
  cmdHint: { color: '#888', fontSize: 12, margin: '6px 0 0' },
  previewOuter: { position: 'relative', marginTop: 12, background: '#eef1f5', border: '1px solid #e3e3e3', borderRadius: 10, padding: 14, display: 'flex', justifyContent: 'center' },
  palette: { position: 'absolute', top: 0, right: 'calc(100% + 12px)', zIndex: 20, width: 168, background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: 10, boxShadow: '0 6px 20px rgba(0,0,0,.18)', overflow: 'hidden', userSelect: 'none' },
  paletteHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px 6px 10px', background: '#1a56db', color: '#fff', cursor: 'default', fontSize: 12, fontWeight: 700 },
  paletteTitle: { letterSpacing: '.02em' },
  paletteMinBtn: { background: 'rgba(255,255,255,.18)', border: 'none', color: '#fff', width: 20, height: 20, borderRadius: 5, cursor: 'pointer', fontSize: 11, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  paletteGrid: { display: 'flex', flexWrap: 'wrap', gap: 6, padding: 8 },
  paletteBtn: { flex: '1 1 46%', display: 'flex', alignItems: 'center', gap: 6, padding: '7px 8px', border: '1px solid #e2e8f0', borderRadius: 7, background: '#f8fafc', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#334155' },
  paletteIcon: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, fontSize: 13, color: '#1a56db' },
  previewDesktop: { width: '100%', background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,.1)' },
  previewMobile: { width: 390, background: '#fff', borderRadius: 26, overflow: 'hidden', boxShadow: '0 6px 24px rgba(0,0,0,.18)', border: '8px solid #111827' },
  previewCol: { display: 'flex', flexDirection: 'column' },
  resizeHandle: { height: 16, marginTop: 8, cursor: 'ns-resize', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#dbe1ea', border: '1px solid #cbd5e1', borderRadius: 6, userSelect: 'none' },
  resizeGrip: { fontSize: 11, fontWeight: 600, color: '#64748b', letterSpacing: 1, lineHeight: 1 },
  iframe: { width: '100%', height: '100%', border: 'none', display: 'block' },
  saveRow: { display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, flexWrap: 'wrap' },
  saveBtn: { background: '#fff', color: '#333', border: '1px solid #ccc', padding: '9px 16px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  saveBtnPrimary: { background: '#047857', color: '#fff', border: 'none', padding: '9px 16px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  pushBtn: { background: '#1a56db', color: '#fff', border: 'none', padding: '9px 16px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  pushOverlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  pushModal: { background: '#fff', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,.3)', width: 'min(520px, 96vw)', maxHeight: '88vh', overflowY: 'auto', padding: '20px 22px' },
  pushTitle: { fontSize: 18, fontWeight: 800, color: '#111827', margin: '0 0 4px' },
  pushLead: { fontSize: 13, color: '#6b7280', margin: '0 0 14px' },
  pushSectionLabel: { fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '.03em', margin: '14px 0 6px' },
  pushStories: { border: '1px solid #e5e7eb', borderRadius: 8, padding: 6, maxHeight: 200, overflowY: 'auto' },
  pushStoryRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 6px', fontSize: 13, color: '#1f2937', cursor: 'pointer' },
  pushOpt: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13, color: '#1f2937', cursor: 'pointer' },
  pushErr: { background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 10px', fontSize: 13, marginTop: 12 },
  pushResults: { marginTop: 12, border: '1px solid #e5e7eb', borderRadius: 8, padding: 8, background: '#f9fafb' },
  pushResultRow: { fontSize: 13, padding: '3px 2px', color: '#1f2937' },
  pushGood: { color: '#047857' },
  pushBad: { color: '#b91c1c' },
  pushActions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 },
  pushCancel: { background: '#fff', color: '#374151', border: '1px solid #d1d5db', padding: '9px 16px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  pushGo: { background: '#1a56db', color: '#fff', border: 'none', padding: '9px 18px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  buildBtn: { background: '#7c3aed', color: '#fff', border: 'none', padding: '9px 16px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', marginLeft: 'auto' },
  buildGo: { background: '#7c3aed', color: '#fff', border: 'none', padding: '9px 18px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  buildSpec: { background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', fontSize: 13, color: '#374151', margin: '2px 0 6px' },
  buildLabel: { fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '.03em', margin: '12px 0 4px' },
  buildPlatforms: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  buildChoice: { flex: '1 1 30%', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 10px', fontSize: 13, cursor: 'pointer', textAlign: 'center', background: '#fff', color: '#374151' },
  buildChoiceOn: { border: '2px solid #7c3aed', color: '#5b21b6', fontWeight: 700, background: '#faf5ff' },
  buildInput: { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 },
  buildNote: { fontSize: 12, color: '#6b7280', margin: '6px 0 0' },
  buildOk: { background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, padding: '10px 12px', marginTop: 12, fontSize: 13, color: '#065f46' },
  buildOkTitle: { fontWeight: 800, marginBottom: 4 },
  buildLink: { display: 'inline-block', margin: '4px 0', color: '#1a56db', fontWeight: 600, wordBreak: 'break-all' },
  buildMeta: { fontSize: 12, color: '#6b7280', marginTop: 4 },
  tokenBox: { background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', marginTop: 12, fontSize: 13, color: '#92400e' },
  tokenLink: { color: '#1a56db', fontWeight: 600 },
};

export default MockupBuilder;
